// What pages do to invoices, in one place so every page does it the same
// way (the phone app's InvoiceActions).
import { formatInvoiceNumber, recordsOf, type Business } from '@/core/business';
import type { Client } from '@/core/client';
import { nowStamp } from '@/core/dates';
import { newId } from '@/core/ids';
import { currencyOfInvoice, type Invoice, type Payment, totalsOf } from '@/core/invoice';
import { amountBand, track } from '@/data/analytics';
import type { SavedItem } from '@/core/savedItem';
import {
  deleteClient,
  deleteInvoice,
  deleteItem,
  getBusiness,
  removeBusiness,
  removePublicInvoice,
  saveClient,
  saveInvoice,
  saveItem,
  takeNextInvoiceNumber,
} from '@/data/store';

/** The next invoice number of [businessId], reserved for good. */
export async function takeNumber(uid: string, businessId: string): Promise<string> {
  const business = await getBusiness(uid, businessId);
  if (!business) throw new Error('No business profile');
  return formatInvoiceNumber(business, await takeNextInvoiceNumber(uid, businessId));
}

/** Adds a payment; a draft that gets paid counts as sent. */
export function recordPayment(uid: string, invoice: Invoice, payment: Payment): Invoice {
  const now = nowStamp();
  const updated: Invoice = { ...invoice, payments: [...invoice.payments, payment], sentAt: invoice.sentAt ?? now, updatedAt: now };
  saveInvoice(uid, updated);
  track('payment_recorded', { method: payment.method, paid_in_full: totalsOf(updated).balance.isZero, currency: invoice.currency });
  return updated;
}

export function removePayment(uid: string, invoice: Invoice, paymentId: string): Invoice {
  const updated: Invoice = { ...invoice, payments: invoice.payments.filter((p) => p.id !== paymentId), updatedAt: nowStamp() };
  saveInvoice(uid, updated);
  track('payment_removed');
  return updated;
}

/** Marks the invoice as sent the first time it leaves the app. */
export function markSent(uid: string, invoice: Invoice, channel: string, format = 'pdf'): Invoice {
  const firstSend = invoice.sentAt === null;
  track('invoice_sent', {
    channel,
    format,
    first_send: firstSend,
    template: invoice.design.templateId,
    amount_band: amountBand(totalsOf(invoice).total.minor),
    currency: currencyOfInvoice(invoice).code,
  });
  if (!firstSend) return invoice;
  const now = nowStamp();
  const updated = { ...invoice, sentAt: now, updatedAt: now };
  saveInvoice(uid, updated);
  return updated;
}

/** Gives the invoice a public page id (once); the page itself is kept current by AppData. */
export function withPublicLink(uid: string, invoice: Invoice): Invoice {
  if (invoice.publicLinkId) return invoice;
  const updated = { ...invoice, publicLinkId: newId(), updatedAt: nowStamp() };
  saveInvoice(uid, updated);
  track('public_link_created');
  return updated;
}

/** Every business's records, for moving or removing a whole business. */
export interface EveryBusiness {
  businesses: Business[];
  invoices: Invoice[];
  clients: Client[];
  items: SavedItem[];
}

/**
 * Moves [fromId]'s invoices, clients and saved items into [intoId], then
 * removes [fromId] (the phone app's BusinessesCubit.merge). Invoices keep
 * their numbers; shared pages follow, published with the new business.
 */
export function mergeBusiness(uid: string, every: EveryBusiness, fromId: string, intoId: string) {
  if (every.businesses.length < 2 || fromId === intoId) return;
  const now = nowStamp();
  for (const invoice of recordsOf(every.invoices, every.businesses, fromId, (i) => i.businessId)) saveInvoice(uid, { ...invoice, businessId: intoId, updatedAt: now });
  for (const client of recordsOf(every.clients, every.businesses, fromId, (c) => c.businessId)) saveClient(uid, { ...client, businessId: intoId, updatedAt: now });
  for (const item of recordsOf(every.items, every.businesses, fromId, (i) => i.businessId)) saveItem(uid, { ...item, businessId: intoId, updatedAt: now });
  removeBusiness(uid, fromId);
  track('business_merged', { business_count: every.businesses.length - 1 });
}

/** Deletes [id] with its invoices (and their shared pages), clients and saved items. */
export function deleteBusiness(uid: string, every: EveryBusiness, id: string) {
  if (every.businesses.length < 2) return;
  for (const invoice of recordsOf(every.invoices, every.businesses, id, (i) => i.businessId)) {
    if (invoice.publicLinkId) removePublicInvoice(invoice.publicLinkId);
    deleteInvoice(uid, invoice.id);
  }
  for (const client of recordsOf(every.clients, every.businesses, id, (c) => c.businessId)) deleteClient(uid, client.id);
  for (const item of recordsOf(every.items, every.businesses, id, (i) => i.businessId)) deleteItem(uid, item.id);
  removeBusiness(uid, id);
  track('business_deleted', { business_count: every.businesses.length - 1 });
}

/** The plain HTML page clients open (public/invoice.html). */
export const publicLinkUrl = (pageId: string) => `${window.location.origin}/i/${pageId}`;

export const businessOrThrow = (business: Business | null | undefined): Business => {
  if (!business) throw new Error('No business profile');
  return business;
};
